import React, { useState } from "react";
import PropTypes from "prop-types";
import { Select, Spin } from "antd";
import { MaterialErrorLabel, MaterialInputSection, MaterialLabel, MaterialLabelFixed } from "../../components/inputs/MaterialStyles";
import { ArrowImg, CloseArrow, SelectArrow, SelectDiv, StyledOption, StyledOptionContent, StyledSelectInput } from "../../components/inputs/InputStyles";
import { withLocalize } from "react-localize-redux";
import ArrowDown from "../../assets/icons/arrow_down.svg";
import Close from "../../assets/icons/close.svg";
import styled from "styled-components";

export const CategoryStyledSelectInput = styled(Select)`
  width: 100%;

  & .ant-select-selection {
    border: 0;
    border-bottom: 1px solid ${p => p.theme.inputBorderColor};
    border-radius: 0px;
    background-color: transparent !important;
    opacity: ${p => (p?.disabled || p?.readOnly) ? 0.6 : 1};
    margin-top: ${({ noLabel, labelFixed }) => noLabel ? '0' : labelFixed ? '5px' : '16px'};
    font-size: 28px;

    &:hover {
      border-bottom: 1px solid ${p => p.theme.inputFocusColor} !important;
      border-color: ${p => p.theme.inputFocusColor} !important;
      cursor: default;
    }

    &:focus {
      border-bottom: 1px solid ${p => p.theme.inputFocusColor} !important;
      border-color: ${p => p.theme.inputFocusColor} !important;
      cursor: default;
    }

    & .ant-select-selection__rendered {
      margin-right: 0px;
      margin-left: 0px;
      line-height: 32px;
      font-size: 28px;

      & .ant-select-selection__placeholder {
        color: rgba(0,0,0,0.65);
        opacity: 1;
        font-size: 28px;
        font-weight: 400;
        line-height: 32px;
        height: 32px;
        top: 0px;
        right: 0px;
        margin-top: 0px;
      }

      & .ant-select-selection-selected-value {
        color: rgba(0,0,0,0.65);
        font-size: 28px;
        font-weight: 400;
        line-height: 32px;
        height: 32px;
        ${({ small }) => small && `
        width: calc(100% - 10px);
        
        & span {
          display: block;
          width: 100%;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        `}
      }
    }
  }

  .ant-select-selection:hover .ant-select-selection__clear {
    opacity: 0;
    font-size: 28px;
  }

  .ant-select-open .ant-select-selection,
  .ant-select-focused .ant-select-selection,
  .ant-select-selection:focus,
  .ant-select-selection:active {
    border-color: ${p => p.theme.inputFocusColor};
    box-shadow: none;
    font-size: 28px;
  }

  ${p =>
    p.error &&
    `.ant-select-selection {
        border: 0;
        border-bottom: 1px solid ${p.theme.inputErrorColor} !important;
        font-size: 28px;
      }
     
  `}
`;

const filterOption = (inputValue, option) => {
  const children = option?.props?.children;
  const text =
    typeof children === "string"
      ? children
      : children?.props?.children ?? "";

  return text.toLowerCase().includes(inputValue.toLowerCase());
};

const CategorySelectInput = ({
  input,
  data,
  dataKey,
  dataLabel,
  placeholder,
  mode,
  notFoundMessage,
  fetching,
  label,
  labelFixed,
  disabled,
  fetchData,
  loading,
  meta,
  translatable,
  activeLanguage,
  allowClear,
  removeSelection,
  tag,
  costumeLabel,
  smallIcon,
  translate
}) => {
  const [filled, setFilled] = useState(input && input.value !== "");
  const { invalid, submitFailed } = meta;
  const showError = invalid && submitFailed;

  const formatValue = value => {
    if (!value) return undefined;
    if (Array.isArray(value)) return value;
    return value.toString();
  };

  let errorMsg = '';
  if (meta?.error) {
    if (Array.isArray(meta.error) && meta.error.length === 3) {
      errorMsg = translate(meta.error[0]).replace(meta.error[1], meta.error[2]);
    }
    else {
      errorMsg = translate(meta?.error);
    }
  }

  const changeSelect = value => {
    input.onChange(value || '');
  };

  return (
    <MaterialInputSection noLabel={!label}>
      <SelectDiv>
        {
          label && (labelFixed ?
            <MaterialLabelFixed tag={tag} filled={filled} showError={showError}>
              {label}
            </MaterialLabelFixed>
            :
            <MaterialLabel tag={tag} filled={filled} showError={showError}>
              {label}
            </MaterialLabel>)
        }
        <CategoryStyledSelectInput
          disabled={disabled}
          mode={mode}
          placeholder={placeholder}
          notFoundContent={fetching ? <Spin size="small" /> : notFoundMessage}
          filterOption={filterOption}
          allowClear={allowClear}
          onSearch={fetchData}
          onChange={changeSelect}
          value={formatValue(input?.value)}
          onBlur={() => setFilled(input && input.value !== "")}
          onFocus={() => setFilled(true)}
          showArrow={false}
          showSearch={true}
          loading={loading}
          error={showError ? 1 : 0}
        >
          {data.map((elem, index) => (
            <StyledOption key={dataKey ? elem[dataKey] : index} disabled={elem.disabled}>
              <StyledOptionContent style={{ fontSize: '22px' }}>
                {costumeLabel
                  ? costumeLabel(elem)
                  : dataLabel
                    ? (translatable ? elem[dataLabel][activeLanguage.code] : elem[dataLabel])
                    : elem}
              </StyledOptionContent>
            </StyledOption>
          ))}
        </CategoryStyledSelectInput>
        {
          allowClear && input?.value
            ? <CloseArrow
              disabled={disabled}
              smallIcon={smallIcon}
              onClick={() => removeSelection(tag)}>
              <ArrowImg src={Close} />
            </CloseArrow>
            : <SelectArrow
              disabled={disabled}
              smallIcon={smallIcon}>
              <ArrowImg src={ArrowDown} />
            </SelectArrow>
        }
      </SelectDiv>
      {
        (showError) &&
        <MaterialErrorLabel visible={showError}>
          {errorMsg.toUpperCase()}
        </MaterialErrorLabel>
      }
    </MaterialInputSection>
  );
};

CategorySelectInput.propTypes = {
  input: PropTypes.object.isRequired,
  meta: PropTypes.object.isRequired,
  data: PropTypes.array.isRequired,
  dataKey: PropTypes.string,
  dataLabel: PropTypes.string,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  fetchData: PropTypes.func,
  loading: PropTypes.bool,
  onPressEnter: PropTypes.func,
  allowClear: PropTypes.bool,
  removeSelection: PropTypes.func,
  tag: PropTypes.string,
  labelFixed: PropTypes.bool,
};

CategorySelectInput.defaultProps = {
  data: [],
  allowClear: false,
  smallIcon: false,
  labelFixed: false,
  dataKey: '_id',
  dataLabel: 'name'
};

export default withLocalize(CategorySelectInput);